import React, { useCallback, useMemo } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import { useEditor } from 'slate-react';
import {
    Spinner,
    Button,
    ButtonGroup,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
} from 'reactstrap';

import { fetcher } from 'api/client';
import { getExternal, clientContractAction } from 'api/actions/contracts';

import { arrayToObject, getIsVariableFilled } from 'utils';
import useFileUpload from 'api/hooks/file/useFileUpload';
import { scrollToNode } from 'components/Editor/Canvas/TratoFormat/editor-utils';
import {
    useSign, HIGHTLIGHT_MISSING_CHECKS, VERIDOC_MODAL, ONETIME_PASSWORD_MODAL,
    CONTROL_MODAL, CONFIRM_UPLOAD_MODAL, HIGHTLIGHT_MISSING_COMPLETE, NEGOTIATE_SIGN_MODAL, SHIELD3_MODAL, FIEL_MODAL,
    SIGN_MODAL, INE_MODAL, VIDEO_CHAT_MODAL, SET_PDF_META, ZOOM_UPLOAD_VIDEO_MODAL, ZOOM_VIDEO_CHAT_MODAL, TOGGLE_ACTIVE_NAV, useSignState, useSignDispatch
} from 'context/signview';
import { useHistory } from 'react-router-dom';


function AliasSelector() {

    const { t } = useTranslation();

    const history = useHistory();

    const { contract } = useSignState();

    const handleChangeParticipant = useCallback((jwt) => {
        history.push(`/sign/${contract._id}/${jwt}`);
    }, [contract._id]);

    const signViewOptions = contract.signViewOptions || {};

    if (signViewOptions.participantAliases?.length <= 1) return null;

    return (
        <UncontrolledDropdown direction="up" className="mr-2">
            <DropdownToggle caret>
                {t('Cambiar Identidad')}
            </DropdownToggle>
            <DropdownMenu>
                {signViewOptions?.participantAliases?.map((p, index) =>
                    <DropdownItem
                        key={`option-${index}`}
                        onClick={() => handleChangeParticipant(p.jwt)}
                        className={clsx(signViewOptions.currentParticipant._id === p._id && 'font-weight-bold')}
                    >
                        {p.label} - {p.name}
                    </DropdownItem>
                )}
            </DropdownMenu>
        </UncontrolledDropdown>
    )
}

export function useSignViewFooterActions() {
    const { t } = useTranslation();
    const editor = useEditor();

    const [{
        attachmentsMissing,
        variablesMissing,
        missingSignatureConfirmations,
        totalSignatureConfirmations,
        signatureConfirmations,
        contractId,
        contract,
        isNom,
        isRfc3161,
        jwt
    }, dispatch] = useSign();
    const signViewOptions = contract.signViewOptions || {};
    const queryClient = useQueryClient();

    const { mutate: mutateUploadPdf, isLoading: uploadLoading } = useMutation((payload) => fetcher(clientContractAction(contractId, 'uploadpdfversion', payload), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract');
        }
    });
    
    const { chooseFileAndUploadFile: chooseFileAndUploadWordFile } = useFileUpload({
        jwt,
        onSuccess: async (wordonline) => {
            dispatch({ type: CONTROL_MODAL, open: CONFIRM_UPLOAD_MODAL, wordonline })
        },
    })

    const { chooseFileAndUploadFile: chooseFileAndUploadPdfFile } = useFileUpload({
        jwt,
        onSuccess: async ({ url, BaseFileName }) => {
            mutateUploadPdf({ url, filename: BaseFileName })
        },
    })

    const { mutate: mutateComplete, isLoading: completeLoading } = useMutation((payload) => fetcher(clientContractAction(contractId, 'complete', { convertDates: true, ...payload }), jwt), {
        onSuccess: () => {
            mutateCheckComplete();
        }
    });
    const { mutate: mutateCheckComplete, isLoading: checkLoading } = useMutation(() => fetcher(clientContractAction(contractId, 'verifycomplete', {}), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract');
        }
    });
    const { mutate: mutateGrabNegotiation, isLoading: grabLoading } = useMutation(() => fetcher(clientContractAction(contractId, 'grabnegotiation', {}), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract');
        }
    });
    const { mutate: mutateAcceptNegotiation, isLoading: acceptLoading } = useMutation((payload) => fetcher(clientContractAction(contractId, 'acceptnegotiation', payload), jwt), {
        onSuccess: () => {
            queryClient.invalidateQueries('client-contract');
        }
    });
    const { mutate: mutateDownloadVersion, isLoading: downloadLoadingVersion } = useMutation((payload) => fetcher(clientContractAction(contractId, 'downloadwordversion', payload), jwt), {
        onSuccess: ({ url }) => {
            window.open(url);
        }
    });
    const { mutate: mutateDownload, isLoading: downloadLoading } = useMutation((payload) => fetcher(getExternal(contractId, payload), jwt), {
        onSuccess: ({ url }) => {
            window.open(url);
        }
    });

    const doAction = useCallback((dispatchAction, handleAction) => {
        if (dispatchAction) dispatch(dispatchAction)
        if (handleAction) handleAction();
    }, [dispatch]);

    const isLoading = completeLoading ||
    checkLoading ||
    grabLoading ||
    acceptLoading ||
    downloadLoadingVersion ||
    uploadLoading ||
    downloadLoading;

    const buttons = useMemo(() => [
        {
            shouldShow: () => signViewOptions.showAwait,
            color: 'secondary',
            icon: 'fas fa-cubes fa-spin',
            disabled: true,
            label: t('blockchain.waiting')
        },
        {
            shouldShow: () => signViewOptions.showPagareOptions,
            color: 'secondary',
            id: 'cancel_pagare',
            dispatchAction: 'cancel-pagare',
            label: t('blockchain.cancel_pagare'),
        },
        {
            shouldShow: () => signViewOptions.showPagareOptions,
            color: 'secondary',
            dispatchAction: 'endose-pagare',
            id: 'endose_pagare',
            label: t('blockchain.endorse_promissory_note'),
        },
        {
            shouldShow: () => signViewOptions.showFundBlockchain,
            color: 'secondary',
            dispatchAction: 'fund-contract',
            id: 'fund_contract',
            label: t('blockchain.fund_contract'),
        },
        {
            shouldShow: () => signViewOptions.showRecibeEndorse,
            dispatchAction: 'recibe-endorse-pagare',
            color: 'secondary',
            id: 'recibe_endorse',
            label: t('blockchain.recibe_endorse'),
        },
        {
            shouldShow: () => signViewOptions.showCreateWallet,
            color: 'secondary',
            dispatchAction: 'create-wallet',
            id: 'create_wallet',
            label: t('blockchain.create-wallet-fiel'),
        },
        {
            shouldShow: () => signViewOptions.showDownloadWord,
            color: 'secondary',
            id: 'download_version',
            handleAction: () => {
                mutateDownloadVersion({ versionId: contract.wordonline?.Version });
            },
            label: t('contracts.negotiation.download_version'),
        },
        {
            shouldShow: () => signViewOptions.showDownload && !signViewOptions.showAwaitComposition,
            color: 'secondary',
            id: 'download',
            handleAction: () => {
                // eslint-disable-next-line no-nested-ternary
                mutateDownload(isRfc3161 ? 'rfc3161' : isNom ? 'nom151' : 'pdf');
            },
            label: t('sign.contract.banner_download_action')
        },
        {
            shouldShow: () => signViewOptions.showSign,
            color: 'primary',
            id: 'show_sign',
            disabled: !!signViewOptions?.currentParticipant?.requireGroupValidation,
            dispatchAction: missingSignatureConfirmations < totalSignatureConfirmations ?
                { type: HIGHTLIGHT_MISSING_CHECKS } :
                { type: CONTROL_MODAL, open: SIGN_MODAL },
            label: missingSignatureConfirmations < totalSignatureConfirmations ?
                t('sign.status.signatures_missing_replace', {
                    replace: {
                        missing: missingSignatureConfirmations,
                        total: totalSignatureConfirmations,
                    }
                })
                : t('sign.contract.proceed_to_sign'),
                handleAction: () => {
                   if (missingSignatureConfirmations < totalSignatureConfirmations) {
                       const [signatureId] = Object.entries(signatureConfirmations).find(([signatureId, isChecked]) => !isChecked)
                        scrollToNode(editor, { _id: signatureId })
                   }
                }
        },
        {
            shouldShow: () => signViewOptions.showComplete,
            color: 'primary',
            id: 'show_complete',
            label: !attachmentsMissing.filter(a => !a.url && a.required).length && !variablesMissing.filter(v => !getIsVariableFilled(v)).length ?
                t('sign.status.all_completed') :
                t('sign.status.several_element_missing',
                    {
                        replace: {
                            incompleteCount: attachmentsMissing.filter(a => !a.url && a.required).length + variablesMissing.filter(v => !getIsVariableFilled(v)).length
                        }
                    }
                ),
            handleAction: () => {
                if (!attachmentsMissing.filter(a => !a.url && a.required).length && !variablesMissing.filter(v => !getIsVariableFilled(v)).length) {
                    mutateComplete(arrayToObject(variablesMissing, 'apiName', '', 'value'));
                } else {
                    dispatch({ type: HIGHTLIGHT_MISSING_COMPLETE });
                    dispatch({ type: TOGGLE_ACTIVE_NAV })
                }
            }
        },
        {
            shouldShow: () => signViewOptions.showContinue,
            color: 'primary',
            id: 'show_continue',
            dispatchAction: {
                type: CONTROL_MODAL,
                open: NEGOTIATE_SIGN_MODAL
            },
            label: t('sign.contract.continue')
        },
        // {
        //     shouldShow: () => signViewOptions.showChange,
        //     class: 'pickled-bluewood-btn',
        //     disabled: true,
        //     dispatchAction: 'change-contract',
        //     label: t('sign.contract.negotiation_proceed')
        // },
        {
            shouldShow: () => signViewOptions.waitingApprovals,
            color: 'primary',
            disabled: true,
            dispatchAction: 'waiting_approvals',
            label: t('sign.contract.continue')
        },
        {
            shouldShow: () => signViewOptions.uploadVersionPdf,
            color: 'primary',
            id: 'upload_pdf_version',
            handleAction: () => {
                chooseFileAndUploadPdfFile({}, {
                    accept: '.pdf'
                });
            },
            label: t('sign.menu.upload')
        },
        {
            shouldShow: () => signViewOptions.uploadVersionWord,
            color: 'primary',
            id: 'upload_word_version',
            handleAction: () => {
                chooseFileAndUploadWordFile({}, {
                    accept: '.docx,.doc'
                })
            },
            label: t('sign.menu.upload')
        },
        {
            shouldShow: () => signViewOptions.showWaitingNegotiation,
            color: 'primary',
            id: 'wait_negotiation',
            disabled: true,
            title: t('sign.header_status.WAITING_TO_NEGOTIATION'),
            dispatchAction: 'waiting-negotiation',
            label: t('sign.contract.continue')
        },
        {
            shouldShow: () => signViewOptions.consentNedded,
            id: 'generate_agreement',
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: SHIELD3_MODAL },
            label: t('sign.sign.generate_agreement')
        },
        {
            shouldShow: () => signViewOptions.waitingConsent,
            id: 'await_consent',
            color: 'primary',
            disabled: true,
            title: t('sign.header_status.WAITING_ID_VALIDATION'),
            label: t('sign.contract.continue')
        },
        {
            shouldShow: () => signViewOptions.showCompleteWaiting,
            color: 'primary',
            id: 'waiting_complete',
            disabled: true,
            title: t('sign.menu.waiting_complete'),
            label: t('sign.contract.continue')
        },
        {
            shouldShow: () => signViewOptions.showCertificateSign,
            color: 'primary',
            id: 'show_certificate',
            dispatchAction: { type: CONTROL_MODAL, open: FIEL_MODAL },
            label: t('sign.contract.proceed_to_sign')
        },
        {
            shouldShow: () => signViewOptions.idValidationNeeded,
            id: 'id_validation',
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: VERIDOC_MODAL, onlyIne: false },
            label: t('contracts.negotiation.idvalidation')
        },
        {
            shouldShow: () => signViewOptions.showOneTimePassword,
            id: 'id_validation',
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: ONETIME_PASSWORD_MODAL },
            label: t('onetimePassword.title')
        },
        {
            shouldShow: () => signViewOptions.showGrabNegotiation,
            id: 'grab_negotiation',
            color: 'secondary',
            handleAction: () => {
                mutateGrabNegotiation();
            },
            label: t('contracts.negotiation.take')
        },
        {
            shouldShow: () => signViewOptions.showGrabNegotiation,
            color: 'primary',
            id: 'accept_grab_negotiation',
            handleAction: () => {
                mutateAcceptNegotiation({ currentVersion: contract.wordonline?.Version });
            },
            label: t('contracts.negotiation.accept')
        },
        {
            shouldShow: () => signViewOptions.acceptVersion,
            color: 'success',
            id: 'accept_version',
            handleAction: () => {
                mutateAcceptNegotiation({ currentVersion: contract.wordonline?.Version });
            },
            label: signViewOptions.isMyVersion ? t('contracts.negotiation.send') : t('contracts.negotiation.accept')
        },
        {
            shouldShow: () => signViewOptions.showWaitingNegotiationDocument,
            color: 'primary',
            disabled: true,
            title: t('sign.header_status.WAITING_TO_NEGOTIATION'),
            dispatchAction: 'waiting-negotiation',
            label: t('contracts.negotiation.inturn', { name: signViewOptions.grabUserName })
        },
        {
            shouldShow: () => signViewOptions.showWaitingOwnerApproval,
            color: 'primary',
            disabled: true,
            label: t('contracts.negotiation.waiting_owner')
        },
        {
            shouldShow: () => signViewOptions.showWaitingOwnerConfiguration,
            color: 'primary',
            disabled: true,
            label: t('contracts.negotiation.waiting_config')
        },
        {
            shouldShow: () => signViewOptions.showShield4,
            color: 'primary',
            disabled: true,
            label: t('contracts.negotiation.fingerprint_through_phone')
        },
        {
            shouldShow: () => signViewOptions.showShield4ValidationNeeded,
            color: 'primary',
            disabled: true,
            label: t('contracts.negotiation.fingerprint_validation')
        },
        {
            shouldShow: () => signViewOptions.showSendId,
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: VERIDOC_MODAL, onlyIne: true },
            label: t('contracts.negotiation.send_ID')
        },
        {
            shouldShow: () => signViewOptions.showIneOCR,
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: INE_MODAL },
            label: t('contracts.negotiation.upload_INE')
        },
        {
            shouldShow: () => signViewOptions.showRecordVideo,
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: VIDEO_CHAT_MODAL },
            label: t('Grabar video')
        },
        {
            shouldShow: () => signViewOptions.showAwaitRecordVideo,
            color: 'primary',
            dispatchAction: { type: CONTROL_MODAL, open: VIDEO_CHAT_MODAL },
            label: t('Unirse a la llamada')
        },
        {
            shouldShow: () => signViewOptions.showZoomRecordVideo,
            color: 'primary',
            // dispatchAction: { type: CONTROL_MODAL, open: ZOOM_VIDEO_CHAT_MODAL },
            handleAction: () => {
                window.open(contract.zoom.meeting.start_url);
            },    
            label: t('Grabar video')
        },
        {
            shouldShow: () => signViewOptions.showJoinZoomRecordVideo,
            color: 'primary',
            // dispatchAction: { type: CONTROL_MODAL, open: ZOOM_VIDEO_CHAT_MODAL },
            handleAction: () => {
                window.open(contract.zoom.meeting.join_url);
            },            
            label: t('Unirse a la llamada')
        },
        {
            shouldShow: () => signViewOptions.showAwaitComposition,
            color: 'primary',
            disabled: true,
            label: t('Esperando generación de documento')
        },
        {
            shouldShow: () => signViewOptions.showAwaitCloudZoomRecording,
            color: 'secondary',
            icon: 'fas fa-cubes fa-spin',
            disabled: true,
            label: t('Esperando respuesta de Zoom')
        },
        {
            shouldShow: () => signViewOptions.showAwaitZoomRecording,
            color: 'secondary',
            icon: 'fas fa-cubes fa-spin',
            disabled: true,
            label: t('Esperando que el anfitrión suba evidencia de Zoom')
        },
        {
            shouldShow: () => signViewOptions.showUploadZoomRecording,
            color: 'primary',
            icon: 'fas fa-cubes fa-spin',
            dispatchAction: { type: CONTROL_MODAL, open: ZOOM_UPLOAD_VIDEO_MODAL },
            label: t('Subir Video')
        },
    ],
        [t, 
        signViewOptions?.currentParticipant?.requireGroupValidation, 
        signViewOptions.isMyVersion, 
        signViewOptions.grabUserName, 
        signViewOptions.showAwait, 
        signViewOptions.showPagareOptions, 
        signViewOptions.showFundBlockchain, 
        signViewOptions.showRecibeEndorse,
        signViewOptions.showCreateWallet, 
        signViewOptions.showDownloadWord, 
        signViewOptions.showDownload, 
        signViewOptions.showAwaitComposition, 
        signViewOptions.showSign, 
        signViewOptions.showComplete, 
        signViewOptions.showContinue, 
        signViewOptions.waitingApprovals, 
        signViewOptions.uploadVersionPdf, 
        signViewOptions.uploadVersionWord, 
        signViewOptions.showWaitingNegotiation, 
        signViewOptions.consentNedded, 
        signViewOptions.waitingConsent, 
        signViewOptions.showCompleteWaiting, 
        signViewOptions.showCertificateSign, 
        signViewOptions.idValidationNeeded, 
        signViewOptions.showGrabNegotiation, 
        signViewOptions.acceptVersion, 
        signViewOptions.showWaitingNegotiationDocument, 
        signViewOptions.showWaitingOwnerApproval, 
        signViewOptions.showWaitingOwnerConfiguration, 
        signViewOptions.showShield4, 
        signViewOptions.showShield4ValidationNeeded, 
        signViewOptions.showSendId, 
        signViewOptions.showIneOCR, 
        signViewOptions.showRecordVideo, 
        signViewOptions.showAwaitRecordVideo,
        signViewOptions.showAwaitZoomRecording,
        signViewOptions.showAwaitCloudZoomRecording,
        contract.wordonline?.Version, 
        missingSignatureConfirmations, 
        totalSignatureConfirmations, 
        attachmentsMissing, 
        variablesMissing, 
        signatureConfirmations,
        isNom, 
        isRfc3161,
        editor, 
        dispatch, 
        chooseFileAndUploadPdfFile, 
        chooseFileAndUploadWordFile, 
        mutateDownload, 
        mutateComplete, 
        mutateDownloadVersion, 
        mutateGrabNegotiation, 
        mutateAcceptNegotiation
    ]);

    return { isLoading, doAction, buttons }
}

export default function Footer() {
    const [{
        contract,
    }] = useSign();

    const signViewOptions = contract.signViewOptions || {};
    
    const { isLoading, buttons, doAction } = useSignViewFooterActions()

    return (
        <div className="sticky-bottom bg-white text-center p-2 border-top border-light d-flex flex-column">
            <div className="p-1">
                <PdfPagination />
            </div>
            <div>
                <ButtonGroup>
                    <AliasSelector />
                    {buttons.map(((b, index) => b.shouldShow() && !signViewOptions.showAwait &&
                        <Button
                            id={b.id}
                            key={`footer-button-${index}`}
                            title={b.title || b.label}
                            disabled={b.disabled || isLoading}
                            color={b.color}
                            className={clsx('mr-2', b.class)}
                            onClick={() => doAction(b.dispatchAction, b.handleAction)}
                        >
                            {isLoading && <Spinner size="sm" color="light" className="mr-2" />}
                            {b.label}
                        </Button>
                    ))}
                </ButtonGroup>
            </div>

        </div>
    )
}

function PdfPagination () {
    const dispatch = useSignDispatch();
    const { pdfMeta, contract } = useSignState();
    const { t } = useTranslation();

    const handlePrevPage = () => {
        dispatch({ type: SET_PDF_META, pdfMeta: { currentPage: Math.max(pdfMeta.currentPage - 1, 0)}  })
    }

    const handleNextPage = () => {
        dispatch({ type: SET_PDF_META, pdfMeta: { currentPage: Math.min(pdfMeta.currentPage + 1, pdfMeta.totalPages)}  })
    }

    if (!contract.importedPdf) {
        return null;
    }

    return (
        <div className={clsx("d-flex justify-content-center  align-items-center")} style={{
            // height: 20,
            // left: '50%',
            // transform: 'translate(-50%,-50%)',
            // top: -40,
        }}>
            <ButtonGroup className="rounded bg-white">
                <Button size="sm" color="light" disabled={pdfMeta.currentPage === 1} onClick={handlePrevPage}><i className="fas fa-caret-left" /></Button>
                <div className="align-self-center d-flex align-items-center justify-content-center" style={{minWidth: 15, minHeight: 20}}>
                    {pdfMeta.totalPages ?
                    <small className="d-flex align-items-center">{pdfMeta.currentPage} {t('common.of')} {pdfMeta.totalPages}</small>
                    :
                        <Spinner size="sm" style={{width:10, height: 10}} />
                    }
                </div>
                <Button size="sm" color="light" disabled={pdfMeta.currentPage === pdfMeta.totalPages} onClick={handleNextPage}><i className="fas fa-caret-right" /></Button>
            </ButtonGroup>
        </div>
    )
}